import React, { useState } from 'react';
import { ArrowRight, CheckCircle, Loader2, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { leadsAPI } from '../lib/api';

export const PillarLeadCapture = ({ pillar, slug }) => {
  const [form, setForm] = useState({ name: '', email: '', goal: '' });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error('Please add your name and email');
      return;
    }
    setLoading(true);
    try {
      await leadsAPI.submit({ ...form, pillar, pillar_slug: slug });
      setDone(true);
      toast.success("You're on the list!");
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="py-16 lg:py-20 px-6 lg:px-12 bg-[#FAF8F7]">
      <motion.div
        className="max-w-4xl mx-auto bg-[#E8D5D1]/40 rounded-[2.5rem] border border-[#E8D5D1] p-8 lg:p-12 relative overflow-hidden"
        initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
        data-testid="pillar-lead-capture"
      >
        <div className="absolute -top-16 -right-16 w-56 h-56 bg-[#C28B87]/20 rounded-full blur-[100px]" />
        <div className="relative">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/70 border border-[#E8D5D1] rounded-full text-[#C28B87] text-sm font-semibold mb-5">
            <Sparkles className="w-4 h-4" /> Early Access
          </div>
          <h2 className="font-display text-3xl sm:text-4xl text-[#2B2625] font-medium mb-3">
            Get early access to your personalized {pillar} plan
          </h2>
          <p className="text-[#6C6361] leading-relaxed max-w-2xl mb-8">
            Join the private beta and be first to receive preventative, personalized {pillar.toLowerCase()} guidance built for your biology.
          </p>

          {done ? (
            <div className="flex items-start gap-4 bg-white rounded-2xl p-6 border border-[#E8D5D1]" data-testid="lead-success">
              <div className="w-12 h-12 rounded-full bg-[#F3EFEF] flex items-center justify-center flex-shrink-0"><CheckCircle className="w-6 h-6 text-[#C28B87]" /></div>
              <div>
                <p className="font-display text-xl text-[#2B2625] font-medium">You&apos;re on the list!</p>
                <p className="text-[#6C6361] text-sm mt-1">We&apos;ll be in touch with early access to your personalized {pillar} plan.</p>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="lead-name" className="text-[#2B2625]">Your Name</Label>
                  <Input id="lead-name" placeholder="Your full name" value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} className="rounded-xl border-[#E8D5D1] focus:border-[#C28B87] bg-white h-12" required data-testid="lead-name-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lead-email" className="text-[#2B2625]">Email Address</Label>
                  <Input id="lead-email" type="email" placeholder="you@example.com" value={form.email} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} className="rounded-xl border-[#E8D5D1] focus:border-[#C28B87] bg-white h-12" required data-testid="lead-email-input" />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="lead-goal" className="text-[#2B2625]">What are you hoping to achieve? <span className="text-[#6C6361]">(optional)</span></Label>
                <Textarea id="lead-goal" placeholder={`Tell us a little about your ${pillar.toLowerCase()} goals...`} value={form.goal} onChange={(e) => setForm((p) => ({ ...p, goal: e.target.value }))} rows={3} className="rounded-xl border-[#E8D5D1] focus:border-[#C28B87] bg-white" data-testid="lead-goal-input" />
              </div>
              <Button type="submit" disabled={loading} className="w-full sm:w-auto bg-[#C28B87] text-white hover:bg-[#b07b77] rounded-full px-8 py-6 text-base font-medium shadow-[0_10px_30px_rgba(194,139,135,0.3)] transition-transform hover:scale-[1.02]" data-testid="lead-submit-btn">
                {loading ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Submitting...</>) : (<>Get Early Access <ArrowRight className="ml-2 w-4 h-4" /></>)}
              </Button>
            </form>
          )}
        </div>
      </motion.div>
    </section>
  );
};
